import { useCallback, useState } from "react";
import { t } from "ttag";

import { useDeepCopyTabMutation } from "metabase/api";
import { Link } from "metabase/common/components/Link";
import { DashboardPickerModal } from "metabase/common/components/Pickers/DashboardPicker";
import { useDispatch } from "metabase/redux";
import { addUndo } from "metabase/redux/undo";
import { Loader, Modal, Stack, Text } from "metabase/ui";
import { color } from "metabase/ui/utils/colors";
import * as Urls from "metabase/urls";
import type {
  Dashboard,
  DashboardId,
  DashboardTabId,
} from "metabase-types/api";

type ModalStage = { kind: "picking" } | { kind: "cloning" };

export function DashboardCloneTabDepsModal({
  dashboardId,
  tabId,
  tabName,
  onClose,
}: {
  dashboardId: DashboardId;
  tabId: DashboardTabId;
  tabName: string;
  onClose: () => void;
}) {
  const [stage, setStage] = useState<ModalStage>({ kind: "picking" });
  const [deepCopyTab] = useDeepCopyTabMutation();
  const dispatch = useDispatch();

  const runClone = useCallback(
    async (targetDashboardId: DashboardId) => {
      setStage({ kind: "cloning" });
      try {
        const result = await deepCopyTab({
          id: dashboardId,
          tab_id: tabId,
          target_dashboard_id: targetDashboardId,
        }).unwrap();
        dispatch(
          addUndo({
            icon: "clone",
            message: (
              <>
                {t`Tab "${tabName}" cloned with ${result.cards_copied} card(s) — `}
                <Link
                  to={Urls.dashboard({ id: targetDashboardId } as Dashboard)}
                  style={{ marginInlineStart: ".25em" }}
                  color={color("brand")}
                >
                  {t`view dashboard`}
                </Link>
              </>
            ),
          }),
        );
        onClose();
      } catch (err: any) {
        const body = err?.data ?? err;
        dispatch(
          addUndo({
            icon: "warning",
            message: t`Clone failed: ${body?.message ?? t`unknown error`}`,
            timeout: 8000,
          }),
        );
        onClose();
      }
    },
    [dashboardId, tabId, tabName, deepCopyTab, dispatch, onClose],
  );

  if (stage.kind === "cloning") {
    return (
      <Modal.Root opened onClose={() => undefined} size="sm">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{t`Cloning tab with dependencies…`}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Stack align="center" gap="md" py="xl">
              <Loader size="xl" />
              <Text c="text-secondary">
                {t`Copying cards and dependencies. This may take a moment.`}
              </Text>
            </Stack>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  return (
    <DashboardPickerModal
      title={t`Clone tab "${tabName}" into…`}
      onChange={(destination) => runClone(destination.id as DashboardId)}
      onClose={onClose}
      options={{
        hasSearch: true,
        hasConfirmButtons: true,
        hasRootCollection: true,
        hasPersonalCollections: true,
      }}
    />
  );
}
